import { Component, Host, Prop, Watch, h } from '@stencil/core';
import {
  getAssetSrc,
  getTenant,
  renderResponsiveImages,
} from '../../helpers/helpers';

import type {
  CssClassMap,
  ResponsiveImages,
} from '../../helpers/utils/interfaces';

@Component({
  tag: 'cms-hero-teaser',
  styleUrl: 'cms-hero-teaser.scss',
  shadow: true,
})
export class CmsHeroTeaser {
  private readonly icon = 'arrowShaftRight';
  private readonly headlineBgImage = 'scratch-scalable.svg';

  private responsiveImages: ResponsiveImages;
  private readonly imageSizes = `
  (min-resolution: 288dpi) 33.33vw,
  (min-resolution: 192dpi) 50vw,
  100vw`;

  /**
   * set image position to center bottom
   */
  @Prop({ reflect: true }) setImageFocusTop: boolean;

  /**
   * set the container background color by breakpoint > 1920px
   */
  @Prop() backgroundColor: string;

  /**
   * set the responsive images
   */
  @Prop() images!: ResponsiveImages | string;

  /**
   * set the images alt text
   */
  @Prop({ mutable: true }) imageAltText = 'Hornbach';

  /**
   * set the hero teaser headline, h2 on home h1 on landing page
   */
  @Prop({ reflect: true }) headline: string;

  /**
   * set the headline tag like h1, h2
   */
  @Prop() headlineTag: 'h1' | 'h2';

  /**
   * set the hero teaser subheadline
   */
  @Prop() subheadline: string;

  /**
   * set the target teaser url
   */
  @Prop({ reflect: true }) url: string;

  /**
   * set the target frame like _self or _blank
   */
  @Prop({ reflect: true }) targetFrame: string;

  @Watch('images')
  private imagesWatcher(newValue: ResponsiveImages | string) {
    if (newValue) {
      this.responsiveImages =
        typeof newValue === 'string' ? JSON.parse(newValue) : newValue;
    }
  }

  private renderTeaserImage() {
    return (
      <div class={'teaser-image-wrapper'}>
        {this.responsiveImages != null
          ? renderResponsiveImages(
              this.responsiveImages,
              this.imageSizes,
              this.imageAltText,
              'hero-teaser-image',
            )
          : ''}
      </div>
    );
  }

  private hostCssClass(): CssClassMap {
    return {
      'has-bg': !!this.backgroundColor,
    };
  }

  connectedCallback() {
    if (this.images) {
      this.imagesWatcher(this.images);
    }
  }

  render() {
    const TagType = this.url ? 'a' : 'div';
    return (
      <Host
        class={this.hostCssClass()}
        style={{
          '--cms-container-background-color': `var(${this.backgroundColor})`,
        }}
      >
        <div class='hero-teaser-inner'>
          <div
            class={'grid-item'}
            style={
              this.setImageFocusTop
                ? { '--cms-image-object-position': 'center center' }
                : { '--cms-image-object-position': 'center top' }
            }
          >
            <TagType
              href={this.url ?? null}
              title={this.imageAltText}
              target={this.targetFrame ?? null}
              class='teaser-link'
            >
              {this.renderTeaserImage()}

              {this.headline || this.subheadline ? (
                <div class={'headlines-container'}>
                  <div
                    class='headlines-wrapper'
                    style={
                      getTenant() === 'hornbach'
                        ? {
                            webkitMaskImage: `url(${getAssetSrc(this.headlineBgImage)})`,
                            maskImage: `url(${getAssetSrc(this.headlineBgImage)})`,
                            webkitMaskRepeat: 'no-repeat',
                            maskRepeat: 'no-repeat',
                            webkitMaskSize: '100% 100%',
                            maskSize: '100% 100%',
                          }
                        : null
                    }
                  >
                    {this.headline ? (
                      <cms-headline
                        style={{
                          '--cms-theme-text-color':
                            'var(--cms-semanticColors-primary-textOnBackground)',
                          '--cms-spacing-bottom': 'var(--cms-spacing-050)',
                          '--cms-spacing-top': '0',
                          '--cms-grid-padding': '0',
                        }}
                        headlineText={this.headline}
                        headlineTag={this.headlineTag}
                        headline-position='left'
                        layout='full'
                        size='xl'
                        font-brand='brand1'
                      />
                    ) : null}

                    {this.subheadline ? (
                      <div
                        style={{
                          color:
                            'var(--cms-semanticColors-primary-textOnBackground)',
                        }}
                        class={'subheadline-wrapper'}
                      >
                        <div class={'subheadline'}>{this.subheadline}</div>
                        {this.url ? (
                          <cms-icon
                            size='xl'
                            color-token='--cms-semanticColors-primary-iconOnBackground'
                            icon={this.icon}
                          />
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </TagType>
          </div>
        </div>
      </Host>
    );
  }
}
